import { HTMLAttributes } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { map } from 'lodash'

import Container from '@/components/base/container'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type TimelineProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    pretitle: string
    title: string
    collection: {
      year: string
      title: string
      paragraph: string
      media: WPMedia
    }[]
  }>

const Timeline = ({ pretitle, title, collection, className, ...props }: TimelineProps) => {
  const [emblaRef] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': {
        slidesToScroll: 2
      }
    }
  })

  return (
    <Section
      className={cn(
        'md:[--col:calc((100%-var(--gutter)*4)/6)]',
        'lg:[--col:calc((100%-var(--gutter)*9)/12)]',
        'overflow-hidden',
        className
      )}
      {...props}
    >
      <Container>
        <div className={cn('grid gap-y-[3rem] lg:hidden')}>
          <div
            className={cn(
              'md:[--col:calc((100%-var(--gutter)*5)/6)]',
              'md:max-w-[calc(var(--gutter)*2+var(--col)*3)]',
              'grid gap-y-[3rem]'
            )}
          >
            {pretitle ? (
              <Typography variant="upper-small" component="h3" className="font-bold uppercase">
                {pretitle}
              </Typography>
            ) : null}

            {title ? (
              <Typography variant="main-title-default" component="h2">
                {title}
              </Typography>
            ) : null}
          </div>
        </div>

        <div ref={emblaRef} className={cn('-mx-margin mt-[5rem] px-margin')}>
          <div className="-ml-margin flex touch-pan-y md:-ml-gutter">
            <div
              className={cn(
                'hidden flex-col lg:flex',
                'min-w-0 shrink-0 grow-0 basis-full pl-gutter',
                'lg:basis-[calc(var(--gutter)*3+var(--col)*4)]'
              )}
            >
              <div
                className={cn(
                  'lg:[--col:calc((100%-var(--gutter)*3)/4)]',
                  'lg:max-w-[calc(var(--gutter)*2+var(--col)*4)]',
                  'xl:max-w-[calc(var(--gutter)*2+var(--col)*3)]',
                  'grid gap-y-[3rem]'
                )}
              >
                {pretitle ? (
                  <Typography variant="upper-small" component="h3" className="font-bold uppercase">
                    {pretitle}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography variant="main-title-default" component="h2">
                    {title}
                  </Typography>
                ) : null}
              </div>
            </div>

            {map(collection, ({ year, title, paragraph, media }, index) => (
              <div
                key={`timeline-item-${index}`}
                className={cn(
                  'min-w-0 shrink-0 grow-0 basis-full pl-margin',
                  'md:basis-[calc(var(--gutter)*2+var(--col)*3)] md:pl-gutter',
                  'lg:basis-[calc(var(--gutter)*3+var(--col)*4)]'
                )}
              >
                <div className="grid gap-y-[5rem] xl:px-[2rem]">
                  <Typography variant="standard" component="h5" className="">
                    {year}
                  </Typography>

                  <div className="py-[2rem]">
                    <div className="relative aspect-square">
                      <Media className="object-cover" {...media} />
                    </div>
                  </div>

                  <div className="grid gap-y-[2rem]">
                    <Typography
                      variant="history-size-body"
                      component="h6"
                      className="font-bold opacity-50"
                    >
                      {title}
                    </Typography>
                    <Typography variant="history-size-body" paragraph className="opacity-50">
                      {paragraph}
                    </Typography>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  )
}

export default Timeline
